import { useState, useMemo, useEffect, useRef } from 'react';
import { Button, Icon, PageHeader, Popover2, Loading } from '@evertrue/et-components';
import { useFluxStore } from '@evertrue/et-flux';
import EverTrue from 'app';
import NavButton from 'components/lists/nav-button';
import AllUsersSource from 'apps/users/sources/all-users-source';
import AllUsersStore from 'apps/users/stores/all-users-store';
import useCadences from '../hooks/use-cadences';
import useTemplates from '../hooks/use-templates';
import useTemplateInstances from '../hooks/use-template-instances';
import useCadenceMutations from '../hooks/use-cadence-mutations';
import useDomainStatus from '../hooks/use-domain-status';
import {
  mapCadenceToListRow,
  mapBulkMessageToListRow,
  mapTemplateToListRow,
  getRoundedPercentage,
} from '../utils/cadence-data-mappers';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CADENCE_STATS_BATCH_QUERY } from '../utils/cadence-queries';
import { CADENCE_TYPE } from '../utils/cadence-enums';
import { hasAutomatedEmailSteps } from '../utils/cadence-step-utils';
import CadenceSharingModal from '../components/sharing/cadence-sharing-modal';
import DuplicateCadenceModal from '../components/duplicate/duplicate-cadence-modal';
import ConfirmModal from '../components/shared/confirm-modal';
import OutreachPlansTable from '../components/outreach-plans-table';
import BulkMessagesTable from '../components/bulk-messages-table';
import CadenceShellsTable from '../components/cadence-shells-table';
import { matchesDateFilter } from '../components/date-filter-popover';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';

/** Merge cadenceStats into list row fields (covers both cadence and bulk message column names). */
const mergeStats = (stats) => {
  if (!stats) return {};
  return {
    // currentPercentage is computed server-side from CadenceMemberSuccessEvent — the same
    // source of truth cadenceMetricProgress uses for the detail page — so the list and detail
    // views agree for every metric type. null when the cadence has no success metric configured.
    goalMetric: stats.currentPercentage ?? null,
    // Override totalContacts/recipients with activeMembers (in-progress count)
    totalContacts: stats.activeMembers ?? null,
    recipients: stats.totalMembers ?? null,
    // Cadence table columns (avg prefix)
    avgDeliveryRate: stats.deliveryRate ?? null,
    avgBounceRate: stats.bounceRate ?? null,
    avgUnsubscribeRate: stats.unsubscribeRate ?? null,
    avgOpenRate: stats.openRate ?? null,
    avgClickRate: stats.clickRate ?? null,
    avgReplyRate: stats.replyRate ?? null,
    // Bulk message table columns (no prefix)
    deliveryRate: stats.deliveryRate ?? null,
    bounceRate: stats.bounceRate ?? null,
    unsubscribeRate: stats.unsubscribeRate ?? null,
    openRate: stats.openRate ?? null,
    clickRate: stats.clickRate ?? null,
    replyRate: stats.replyRate ?? null,
  };
};
import { matchesNumericFilter } from '../components/numeric-filter-popover';
import FilterBar from '../components/filter-bar';

const ALL_TABS = [
  { id: 'outreach', label: 'Cadences & Plans' },
  { id: 'bulk', label: 'Bulk Messages' },
  { id: 'templates', label: 'Cadence Blueprints' },
];

const STATUS_OPTIONS_BY_TAB = {
  outreach: [
    { label: 'Active', value: 'Active' },
    { label: 'Draft', value: 'Draft' },
    { label: 'Paused', value: 'Paused' },
    { label: 'Archived', value: 'Archived' },
  ],
  bulk: [
    { label: 'Sent', value: 'Sent' },
    { label: 'Sending', value: 'Sending' },
    { label: 'Scheduled', value: 'Scheduled' },
    { label: 'Draft', value: 'Draft' },
    { label: 'Archived', value: 'Archived' },
  ],
  templates: [
    { label: 'Active', value: 'Active' },
    { label: 'Draft', value: 'Draft' },
    { label: 'Archived', value: 'Archived' },
  ],
};

const DEFAULT_DATE_FILTER = { operator: 'all', date1: null, date2: null };
const DEFAULT_NUMERIC_FILTER = { operator: 'all', value1: null, value2: null };

const METRIC_OPTIONS = [
  { label: 'Email Opened', value: 'email_opened' },
  { label: 'Email Replied', value: 'email_replied' },
  { label: 'Email Clicked', value: 'email_clicked' },
  { label: 'Interaction Created', value: 'interaction_created' },
  { label: 'Gift Given', value: 'gift_given' },
  { label: 'Portfolio Stage Reached', value: 'portfolio_stage_reached' },
  { label: 'Saved Search', value: 'saved_search' },
];

const CADENCE_TYPE_OPTIONS = [
  { label: 'Cadence', value: 'Cadence' },
  { label: 'Prospect Plan', value: 'Prospect Plan' },
];

const BLUEPRINT_TYPE_OPTIONS = [
  { label: 'Cadence', value: 'Cadence' },
  { label: 'Prospect Plan', value: 'Prospect Plan' },
  { label: 'Bulk Message', value: 'Bulk Message' },
];

/**
 * Main controller for Cadences 2.0 page.
 * Thin orchestration shell: hooks, data derivation, tab/filter state, modals.
 * Delegates table rendering to child components using SDK FlexTable.
 */
const CadencesV2Controller = () => {
  const { flagEnabled: hasBulkMessaging } = useFeatureFlag('bulkMessaging');
  const tabs = hasBulkMessaging ? ALL_TABS : ALL_TABS.filter((t) => t.id !== 'bulk');
  const { cadences, loading: cadencesLoading, error: cadencesError, refetch: refetchCadences } = useCadences();
  const {
    templates: rawTemplates,
    loading: templatesLoading,
    error: templatesError,
    refetch: refetchTemplates,
  } = useTemplates();
  const {
    deleteCadence,
    updateCadence,
    duplicateCadence,
    saveCadenceAsTemplate,
    createCadenceFromTemplate,
    activateCadence,
    fetchDeletedCadences,
    restoreCadence,
    createTaskCleanupJob,
  } = useCadenceMutations();

  const { status: domainStatus, domain: postmarkDomain } = useDomainStatus();
  const allUsers = useFluxStore(AllUsersStore, () => AllUsersStore.getAllUsers());
  const usersLoading = useFluxStore(AllUsersStore, () => AllUsersStore.getLoading());

  // Kick off a full user fetch on mount so the Created By filter always has data,
  // even if the app-level fetch hasn't completed or returned partial results.
  useEffect(() => {
    AllUsersSource.fetchAllUsers();
  }, []);

  // --- Auto-resume banner state ---
  // Track dns-paused cadences in localStorage so we can show a count after they're resumed.
  const DNS_PAUSE_KEY = 'cadence_dns_pause_event';
  const DNS_RESUME_DISMISSED_KEY = 'cadence_dns_resume_dismissed';
  const [autoResumeBannerDismissed, setAutoResumeBannerDismissed] = useState(
    () => localStorage.getItem(DNS_RESUME_DISMISSED_KEY) === 'dismissed'
  );

  useEffect(() => {
    // hasAutomatedEmailSteps guard (NEXDEV-2031): a stale/incorrect pausedReason
    // must never count toward this banner for a cadence with no automated steps.
    const dnsPaused = cadences.filter(
      (c) => c.pausedReason === 'dns_disconnect' && hasAutomatedEmailSteps(c.events || [])
    );
    if (dnsPaused.length > 0) {
      const domainName = postmarkDomain?.name || '';
      localStorage.setItem(DNS_PAUSE_KEY, JSON.stringify({ count: dnsPaused.length, domainName }));
      // New disconnect event — clear dismissal so banner shows again on reconnect
      localStorage.removeItem(DNS_RESUME_DISMISSED_KEY);
      setAutoResumeBannerDismissed(false);
    }
  }, [cadences, postmarkDomain]);

  const handleDismissAutoResumeBanner = () => {
    localStorage.setItem(DNS_RESUME_DISMISSED_KEY, 'dismissed');
    setAutoResumeBannerDismissed(true);
  };

  const dnsPauseEvent = (() => {
    try {
      return JSON.parse(localStorage.getItem(DNS_PAUSE_KEY) || 'null');
    } catch {
      return null;
    }
  })();

  const showAutoResumeBanner = domainStatus === 'configured' && dnsPauseEvent?.count > 0 && !autoResumeBannerDismissed;

  useEffect(() => {
    const ids = [
      ...cadences.map((c) => c.ownerUserId),
      ...cadences.map((c) => c.createdByUserId),
      ...rawTemplates.map((t) => t.ownerUserId),
      ...rawTemplates.map((t) => t.createdByUserId),
    ].filter(Boolean);
    if (ids.length > 0) AllUsersSource.fetchUsers(ids);
  }, [cadences, rawTemplates]);

  // --- Tab state ---
  const [selectedTab, setSelectedTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (['outreach', 'bulk', 'templates'].includes(tab)) return tab;
    if (tab === 'shells') return 'templates';
    return 'outreach';
  });

  // --- Blueprint filter from URL (e.g. /cadences-v2?blueprint=123) ---
  const [blueprintFilter, setBlueprintFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('blueprint') || null;
  });
  // useCadences() only ever returns cadences the current viewer can open, so a
  // blueprint-filtered list would silently drop instances someone else created
  // (e.g. an org-wide blueprint's owner viewing a cadence a different user built
  // from it). cadenceTemplateInstances returns every instance regardless of the
  // viewer's permission on each one — used below to fill in what useCadences dropped.
  const { instances: blueprintInstances } = useTemplateInstances(blueprintFilter);
  const isInitialMount = useRef(true);

  // Clear selection and reset filters when switching tabs
  useEffect(() => {
    setSelectedRows([]);
    setStatusFilter([]);
    setTypeFilter([]);
    setTitleFilter('');
    setMetricFilter([]);
    setSelectedTags([]);
    setDateFilter(DEFAULT_DATE_FILTER);
    setSendDateFilter(DEFAULT_DATE_FILTER);
    setNumericFilter(DEFAULT_NUMERIC_FILTER);
    // Preserve blueprint filter on initial mount (set from URL param)
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setBlueprintFilter(null);
    }
    // Default "Created By" to "You" for outreach/bulk, clear for blueprints
    if (selectedTab === 'templates') {
      setOwnerFilter([]);
    } else {
      const myOption = allOwnerOptions.find((o) => o.value === Number(currentUserId));
      setOwnerFilter(myOption ? [myOption] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  // --- Filter state ---
  const [statusFilter, setStatusFilter] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState(DEFAULT_DATE_FILTER);
  const [sendDateFilter, setSendDateFilter] = useState(DEFAULT_DATE_FILTER);
  const [metricFilter, setMetricFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [titleFilter, setTitleFilter] = useState('');
  const [numericFilter, setNumericFilter] = useState(DEFAULT_NUMERIC_FILTER);

  // --- Rename state ---
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // --- Prompt bar state (save-as-shell / create-from-shell) ---
  const [promptAction, setPromptAction] = useState(null);
  const [promptValue, setPromptValue] = useState('');

  // --- Modal state ---
  const [showShareModal, setShowShareModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);

  // --- Bulk selection state ---
  const [selectedRows, setSelectedRows] = useState([]);

  // --- Archived cadences state ---
  const [archivedCadences, setArchivedCadences] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  // Fetch archived cadences when Archived is selected in status filter
  const hasArchivedFilter = statusFilter.some((s) => s.value === 'Archived');
  useEffect(() => {
    if (hasArchivedFilter) {
      setArchivedLoading(true);
      fetchDeletedCadences()
        .then((data) =>
          setArchivedCadences(data.map((c) => ({ ...c, cadenceType: c.cadenceType?.toLowerCase() || c.cadenceType })))
        )
        .catch(() => setArchivedCadences([]))
        .finally(() => setArchivedLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasArchivedFilter]);

  const currentUserId = EverTrue.store?.user?.get('id');

  // --- Derived data ---
  const allTags = useMemo(() => {
    const tagSet = new Set();
    [...cadences, ...rawTemplates].forEach((item) => (item.tags || []).forEach((t) => tagSet.add(t)));
    return [...tagSet].sort();
  }, [cadences, rawTemplates]);

  const allOwnerOptions = useMemo(() => {
    const ownerMap = {};
    [...cadences, ...rawTemplates].forEach((c) => {
      // Include both ownerUserId and createdByUserId so the "Created By" filter
      // shows all relevant users even when the two IDs differ.
      [c.ownerUserId, c.createdByUserId].forEach((uid) => {
        if (uid && allUsers[uid]?.name) {
          ownerMap[uid] = allUsers[uid].name;
        }
      });
    });
    // If cadences exist but no owner names resolved yet (users still loading),
    // include the current user so the default "You" filter still works.
    // Only apply the fallback when the current user is actually an owner of one
    // of the cadences — otherwise it incorrectly filters out cadences owned by
    // other users (e.g. local dev seed data owned by a different user ID).
    const currentUserIsOwner = [...cadences, ...rawTemplates].some(
      (c) => c.ownerUserId === currentUserId || c.createdByUserId === currentUserId
    );
    if (Object.keys(ownerMap).length === 0 && currentUserIsOwner && allUsers[currentUserId]?.name) {
      ownerMap[currentUserId] = allUsers[currentUserId].name;
    }
    return Object.entries(ownerMap)
      .map(([id, name]) => ({ label: name, value: Number(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cadences, rawTemplates, allUsers, currentUserId]);

  const tagOptions = useMemo(() => allTags.map((t) => ({ label: t, value: t })), [allTags]);

  // Default "Created By" filter to current user ("You") on initial load (outreach/bulk only).
  // Skip when blueprint filter is active — show all cadences from that blueprint regardless of owner.
  const [ownerFilterInitialized, setOwnerFilterInitialized] = useState(false);
  useEffect(() => {
    if (
      !ownerFilterInitialized &&
      currentUserId &&
      allOwnerOptions.length > 0 &&
      selectedTab !== 'templates' &&
      !blueprintFilter
    ) {
      const myOption = allOwnerOptions.find((o) => o.value === Number(currentUserId));
      if (myOption) {
        setOwnerFilter([myOption]);
      }
      setOwnerFilterInitialized(true);
    }
  }, [ownerFilterInitialized, currentUserId, allOwnerOptions, selectedTab, blueprintFilter]);

  const templatesMap = useMemo(() => {
    const map = {};
    rawTemplates.forEach((t) => {
      map[t.id] = t.name;
    });
    return map;
  }, [rawTemplates]);

  const blueprintOptions = useMemo(
    () => Object.entries(templatesMap).map(([id, name]) => ({ value: id, label: name })),
    [templatesMap]
  );

  // Batch-fetch stats for all cadences and blueprints in one request — they share the same
  // resolver since blueprints are just cadences with isTemplate=true.
  const [statsMap, setStatsMap] = useState({});
  useEffect(() => {
    const ids = [
      ...cadences.filter((c) => !c.isTemplate && c.id != null).map((c) => String(c.id)),
      ...rawTemplates.filter((t) => t.id != null).map((t) => String(t.id)),
    ];
    if (!ids.length) {
      setStatsMap({});
      return;
    }
    let cancelled = false;
    cadenceGqlCall(CADENCE_STATS_BATCH_QUERY, { cadenceIds: ids })
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data.cadenceStatsBatch || []).forEach((s) => {
          if (s?.cadenceId) map[String(s.cadenceId)] = s;
        });
        setStatsMap(map);
      })
      .catch(() => {
        if (!cancelled) setStatsMap({});
      });
    return () => {
      cancelled = true;
    };
  }, [cadences, rawTemplates]);

  // Live cadences — exclude archived (they come from the separate fetchDeletedCadences path).
  // Without this, a cadence archived from the detail page stays in the list with stale status
  // until the page is hard-refreshed, because useCadences doesn't re-fetch on navigation back.
  //
  // When filtering by blueprint, fill in any instance useCadences() dropped (see
  // blueprintInstances above) so the list stays accurate even for cadences the
  // current viewer can't personally open — those rows render disabled (TitleCell
  // checks canView) rather than vanishing.
  const liveCadences = useMemo(() => {
    const base = cadences.filter((c) => (c.displayStatusLabel || '').toLowerCase() !== 'archived');
    if (!blueprintFilter) return base;
    const knownIds = new Set(base.map((c) => String(c.id)));
    const missing = blueprintInstances.filter(
      (c) => String(c.sourceCadenceTemplateId) === String(blueprintFilter) && !knownIds.has(String(c.id))
    );
    return [...base, ...missing];
  }, [cadences, blueprintFilter, blueprintInstances]);

  // Names for the merged-in rows above — they're not part of `cadences`, so the
  // general AllUsersSource.fetchUsers(ids) effect never picks up their owners.
  useEffect(() => {
    const ids = blueprintInstances.flatMap((c) => [c.ownerUserId, c.createdByUserId]).filter(Boolean);
    if (ids.length > 0) AllUsersSource.fetchUsers(ids);
  }, [blueprintInstances]);

  const outreachPlans = useMemo(
    () =>
      liveCadences
        .filter(
          (c) =>
            (c.cadenceType === CADENCE_TYPE.CADENCE || c.cadenceType === CADENCE_TYPE.PROSPECT_PLAN) && !c.isTemplate
        )
        .map((c) => ({
          ...mapCadenceToListRow(c, allUsers, templatesMap),
          ...mergeStats(statsMap[String(c.id)]),
        })),
    [liveCadences, allUsers, templatesMap, statsMap]
  );

  const bulkMessages = useMemo(
    () =>
      liveCadences
        .filter((c) => c.cadenceType === CADENCE_TYPE.BULK_MESSAGE && !c.isTemplate)
        .map((c) => ({
          ...mapBulkMessageToListRow(c, allUsers, templatesMap),
          ...mergeStats(statsMap[String(c.id)]),
        })),
    [liveCadences, allUsers, templatesMap, statsMap]
  );

  const templates = useMemo(
    () =>
      rawTemplates.map((t) => {
        const row = mapTemplateToListRow(t, allUsers);
        const stats = statsMap[String(t.id)];
        if (stats) {
          const avgPerf = getRoundedPercentage(stats.qualifiedMembers ?? 0, stats.totalMembers ?? 0);
          return {
            ...row,
            typicalPerformance: avgPerf,
            performanceLabel: row.goal || null,
            avgDeliveryRate: stats.deliveryRate ?? null,
            avgBounceRate: stats.bounceRate ?? null,
            avgUnsubscribeRate: stats.unsubscribeRate ?? null,
            avgOpenRate: stats.openRate ?? null,
            avgClickRate: stats.clickRate ?? null,
            avgReplyRate: stats.replyRate ?? null,
          };
        }
        return row;
      }),
    [rawTemplates, allUsers, statsMap]
  );

  // Archived cadences mapped per tab
  const archivedOutreachPlans = useMemo(
    () =>
      archivedCadences
        .filter(
          (c) =>
            (c.cadenceType === CADENCE_TYPE.CADENCE || c.cadenceType === CADENCE_TYPE.PROSPECT_PLAN) && !c.isTemplate
        )
        .map((c) => ({ ...mapCadenceToListRow(c, allUsers, templatesMap), status: 'Archived', _isArchived: true })),
    [archivedCadences, allUsers, templatesMap]
  );

  const archivedBulkMessages = useMemo(
    () =>
      archivedCadences
        .filter((c) => c.cadenceType === CADENCE_TYPE.BULK_MESSAGE && !c.isTemplate)
        .map((c) => ({ ...mapBulkMessageToListRow(c, allUsers, templatesMap), status: 'Archived', _isArchived: true })),
    [archivedCadences, allUsers, templatesMap]
  );

  const archivedTemplates = useMemo(
    () =>
      archivedCadences
        .filter((c) => c.isTemplate)
        .map((c) => ({ ...mapTemplateToListRow(c, allUsers), status: 'Archived', _isArchived: true })),
    [archivedCadences, allUsers]
  );

  const isLoading =
    cadencesLoading || (selectedTab === 'templates' && templatesLoading) || (hasArchivedFilter && archivedLoading);
  const fetchError = selectedTab === 'templates' ? templatesError : cadencesError;

  /** Merge archived data in when Archived is selected in status filter. */
  const mergeData = (liveData, archivedData) => {
    if (!hasArchivedFilter) return liveData;
    if (statusFilter.length === 1) return archivedData;
    return [...liveData, ...archivedData];
  };

  /** Apply all filters. */
  const applyFilters = (data) =>
    data.filter((item) => {
      if (statusFilter.length > 0) {
        if (!statusFilter.some((s) => s.value === item.status)) return false;
      } else if (item.status === 'Archived') {
        return false;
      }
      if (typeFilter.length > 0 && !typeFilter.some((t) => t.value === item.type)) return false;
      if (selectedTags.length > 0) {
        const itemTags = item._raw?.tags || [];
        if (!selectedTags.some((t) => itemTags.includes(t.value))) return false;
      }
      if (ownerFilter.length > 0) {
        const ownerId = item._raw?.ownerUserId;
        if (!ownerFilter.some((o) => String(o.value) === String(ownerId))) return false;
      }
      if (!matchesDateFilter(item._raw?.createdAt, dateFilter)) return false;
      if (selectedTab === 'bulk' && !matchesDateFilter(item._raw?.scheduledSendDate, sendDateFilter)) return false;
      if (titleFilter) {
        const name = (item.title || item.name || '').toLowerCase();
        if (!name.includes(titleFilter.toLowerCase())) return false;
      }
      if (metricFilter.length > 0) {
        const metrics = item._raw?.successMetrics || [];
        // Normalize legacy metric keys so old cadences match the filter options
        const LEGACY_TO_NEW = {
          email_open_rate: 'email_opened',
          email_click_rate: 'email_clicked',
          email_response_rate: 'email_replied',
        };
        // Check successMetrics array first (raw metric types from API)
        const matchesRawMetric = metricFilter.some((m) =>
          metrics.some((metric) => {
            const key = (metric.metricType || '').toLowerCase();
            const normalized = LEGACY_TO_NEW[key] || key;
            return normalized === m.value;
          })
        );
        // Fallback: derive metric type from goal label.
        // Backend formats labels as metric_type.replace("_"," ").title() (e.g. "Email Opened"),
        // except saved_search which becomes "Contacts qualify for '...'".
        const goalLabel = item.goal || '';
        let derivedMetricType = '';
        if (goalLabel.toLowerCase().startsWith('contacts qualify for')) {
          derivedMetricType = 'saved_search';
        } else if (goalLabel) {
          derivedMetricType = goalLabel.toLowerCase().replace(/ /g, '_');
          derivedMetricType = LEGACY_TO_NEW[derivedMetricType] || derivedMetricType;
        }
        const matchesGoalMetric = derivedMetricType && metricFilter.some((m) => derivedMetricType === m.value);
        if (!matchesRawMetric && !matchesGoalMetric) return false;
      }
      // Numeric filter — applies to different fields per tab
      if (numericFilter.operator !== 'all') {
        let numVal = null;
        if (selectedTab === 'outreach') numVal = item.totalContacts;
        else if (selectedTab === 'bulk') numVal = item.recipients;
        else if (selectedTab === 'templates') numVal = item.totalCadences;
        if (!matchesNumericFilter(numVal, numericFilter)) return false;
      }
      // Blueprint filter from URL — show only cadences created from this blueprint
      if (blueprintFilter) {
        if (String(item._raw?.sourceCadenceTemplateId) !== String(blueprintFilter)) return false;
      }
      return true;
    });

  const filteredOutreachPlans = applyFilters(mergeData(outreachPlans, archivedOutreachPlans));
  const filteredBulkMessages = applyFilters(mergeData(bulkMessages, archivedBulkMessages));
  const filteredTemplates = applyFilters(mergeData(templates, archivedTemplates));

  // --- Action handlers ---
  const handleCreateNew = (type) => {
    if (type === 'cadence') EverTrue.Navigator('/cadences-v2/create/cadence', { trigger: true });
    else if (type === 'prospect-plan') EverTrue.Navigator('/cadences-v2/create/prospect-plan', { trigger: true });
    else if (type === 'bulk-message') EverTrue.Navigator('/cadences-v2/bulk-message/create', { trigger: true });
    else if (type === 'blueprint') EverTrue.Navigator('/cadences-v2/create/blueprint', { trigger: true });
  };

  const handleView = (type, id) => {
    if (type === 'cadence') EverTrue.Navigator(`/cadences-v2/${id}`, { trigger: true });
    else if (type === 'template') EverTrue.Navigator(`/cadences-v2/template/${id}`, { trigger: true });
    else if (type === 'bulk-message') EverTrue.Navigator(`/cadences-v2/bulk-message/${id}`, { trigger: true });
  };

  const handleEdit = (type, id) => {
    if (type === 'bulk-message') EverTrue.Navigator(`/cadences-v2/bulk-message/${id}/edit`, { trigger: true });
    else EverTrue.Navigator(`/cadences-v2/${id}/edit`, { trigger: true });
  };

  const handleDuplicate = (type, id, opts = {}) => {
    const allItems = [...cadences, ...rawTemplates, ...archivedCadences];
    const item = allItems.find((c) => String(c.id) === String(id));
    setDuplicateTarget({ type, id, name: item?.name || 'Untitled', archived: !!opts.archived });
  };

  const handleDuplicateConfirm = async (newName, options) => {
    const { id } = duplicateTarget;
    try {
      const newCadence = await duplicateCadence(id, newName, options);
      EverTrue.Alert.success('Duplicated successfully');
      refetchCadences();
      refetchTemplates();
      if (newCadence?.id) {
        const type = duplicateTarget.type;
        if (type === 'bulk-message') {
          EverTrue.Navigator(`/cadences-v2/bulk-message/${newCadence.id}`, { trigger: true });
        } else if (type === 'template') {
          EverTrue.Navigator(`/cadences-v2/template/${newCadence.id}`, { trigger: true });
        } else {
          EverTrue.Navigator(`/cadences-v2/${newCadence.id}`, { trigger: true });
        }
      }
    } catch (err) {
      EverTrue.Alert.error(`Duplicate failed: ${err.message}`);
      throw err;
    }
  };

  const handleDelete = (type, id) => {
    const allItems = [...cadences, ...rawTemplates];
    const item = allItems.find((c) => String(c.id) === String(id));
    if (!item) {
      EverTrue.Alert.error('Item not found');
      return;
    }
    deleteCadence(id, item.version)
      .then(() => {
        EverTrue.Alert.success('Archived successfully. Cleaning up incomplete tasks...');
        createTaskCleanupJob([id]).catch((e) => console.warn('[cadences] background fetch failed:', e.message));
        refetchCadences();
        refetchTemplates();
      })
      .catch((err) => EverTrue.Alert.error(`Archive failed: ${err.message}`));
  };

  const handleRestore = (type, id) => {
    restoreCadence(id)
      .then(() => {
        EverTrue.Alert.success('Restored successfully');
        setArchivedCadences((prev) => prev.filter((c) => String(c.id) !== String(id)));
        refetchCadences();
        refetchTemplates();
      })
      .catch((err) => EverTrue.Alert.error(`Restore failed: ${err.message}`));
  };

  /**
   * Shared helper: transition a cadence to a new status.
   * Uses activateCadence for draft→active (has validation gates),
   * updateCadence for all other transitions (paused→active, active→paused, etc.).
   */
  const setCadenceStatus = async (id, currentStatus, newStatus) => {
    const allItems = [...cadences, ...rawTemplates];
    const item = allItems.find((c) => String(c.id) === String(id));
    const version = item?.version;
    if (currentStatus?.toLowerCase() === 'draft' && newStatus === 'active') {
      const hasEmailSteps = hasAutomatedEmailSteps(item?.events || item?._raw?.events || []);
      if (hasEmailSteps && domainStatus === 'not_configured') {
        throw new Error(
          'Email sending requires a verified domain. Ask your organization Owner to configure a sending domain in Organization Settings.'
        );
      }
      if (hasEmailSteps && domainStatus === 'pending') {
        throw new Error(
          "Your organization's sending domain is not yet verified. DNS verification can take up to 48 hours. Try again once verification is complete."
        );
      }
      const result = await activateCadence(id, version);
      if (!result?.success) throw new Error(result?.message || 'Activation failed');
      return result;
    }
    const result = await updateCadence(id, version, { status: newStatus });
    if (newStatus === 'active' && !result?.success) throw new Error(result?.message || 'Could not resume cadence.');
    return result;
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus?.toLowerCase() === 'active' ? 'paused' : 'active';
    try {
      await setCadenceStatus(id, currentStatus, newStatus);
      EverTrue.Alert.success(`Cadence ${newStatus === 'active' ? 'activated' : 'paused'}`);
      refetchCadences();
    } catch (err) {
      if (err.code === 'ATTACHMENT_TOO_LARGE') {
        EverTrue.Alert.error(
          currentStatus?.toLowerCase() === 'paused'
            ? 'Activation Failed: One or more automated email steps include an attachment that is over the 7MB limit. This cadence cannot be re-activated.'
            : 'Activation Failed: One or more automated email steps include an attachment that is over the 7MB limit. Please remove / reduce the size of these attachments in order to publish.'
        );
      } else {
        EverTrue.Alert.error(`Status change failed: ${err.message}`);
      }
    }
  };

  const handleStartRename = (id, currentName) => {
    setRenamingId(id);
    setRenameValue(currentName);
  };

  const handleSubmitRename = async (id) => {
    const newName = renameValue.trim();
    if (!newName) {
      setRenamingId(null);
      return;
    }
    const allItems = [...cadences, ...rawTemplates];
    const item = allItems.find((c) => String(c.id) === String(id));
    if (!item) {
      EverTrue.Alert.error('Item not found');
      setRenamingId(null);
      return;
    }
    try {
      await updateCadence(id, item.version, { name: newName });
      EverTrue.Alert.success('Renamed successfully');
      refetchCadences();
      refetchTemplates();
    } catch (err) {
      EverTrue.Alert.error(`Rename failed: ${err.message}`);
    }
    setRenamingId(null);
  };

  const handleSaveAsShell = (id, currentName) => {
    setPromptAction({ type: 'shell', id });
    setPromptValue(`${currentName} (Blueprint)`);
  };

  const handleCreateFromShell = (templateId, cadenceType) => {
    setPromptAction({ type: 'fromShell', id: templateId, cadenceType });
    setPromptValue('');
  };

  const handlePromptSubmit = async () => {
    const name = promptValue.trim();
    if (!name || !promptAction) return;
    const { type, id } = promptAction;
    setPromptAction(null);
    try {
      if (type === 'shell') {
        await saveCadenceAsTemplate(id, { name });
        EverTrue.Alert.success('Saved as cadence blueprint');
        refetchTemplates();
      } else if (type === 'fromShell') {
        const { cadenceType } = promptAction;
        const result = await createCadenceFromTemplate(id, {
          name,
          status: 'draft',
          ...(cadenceType ? { cadenceType } : {}),
        });
        EverTrue.Alert.success('Cadence created from blueprint');
        refetchCadences();
        refetchTemplates();
        if (result?.cadence?.id) {
          const isBulk = (result.cadence.cadenceType || cadenceType) === 'bulk_message';
          const route = isBulk
            ? `/cadences-v2/bulk-message/${result.cadence.id}/edit`
            : `/cadences-v2/${result.cadence.id}`;
          EverTrue.Navigator(route, { trigger: true });
        }
      }
    } catch (err) {
      EverTrue.Alert.error(`Action failed: ${err.message}`);
    }
  };

  const handleShare = (id, name) => {
    const allItems = [...cadences, ...rawTemplates];
    const rawCadence = allItems.find((c) => String(c.id) === String(id));
    setShareTarget({ id, rawCadence });
    setShowShareModal(true);
  };

  // --- Bulk action handlers ---
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    setConfirmConfig({
      title: `Archive ${count} Item${count > 1 ? 's' : ''}`,
      body: `Incomplete tasks will be removed. You can restore the cadence${count > 1 ? 's' : ''} later.`,
      confirmLabel: 'Archive',
      isDanger: true,
      onConfirm: () => {
        const allItems = [...cadences, ...rawTemplates];
        Promise.allSettled(
          selectedRows.map((row) => {
            const item = allItems.find((c) => String(c.id) === String(row.id));
            if (!item) return Promise.resolve();
            return deleteCadence(row.id, item.version);
          })
        ).then((results) => {
          const failed = results.filter((r) => r.status === 'rejected').length;
          const successIds = results
            .map((r, i) => (r.status === 'fulfilled' ? selectedRows[i].id : null))
            .filter(Boolean);
          if (failed > 0) EverTrue.Alert.error(`${failed} of ${count} archives failed`);
          else EverTrue.Alert.success(`${count} item${count > 1 ? 's' : ''} archived. Cleaning up incomplete tasks...`);
          if (successIds.length > 0) {
            createTaskCleanupJob(successIds).catch((e) =>
              console.warn('[cadences] background fetch failed:', e.message)
            );
          }
          setSelectedRows([]);
          refetchCadences();
          refetchTemplates();
        });
      },
    });
  };

  const handleBulkPause = () => {
    const activeRows = selectedRows.filter((row) => row.status?.toLowerCase() === 'active' && !!row._raw?.canDelete);
    if (activeRows.length === 0) return;
    const count = activeRows.length;
    Promise.allSettled(activeRows.map((row) => setCadenceStatus(row.id, row.status, 'paused'))).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) EverTrue.Alert.error(`${failed} of ${count} pauses failed`);
      else EverTrue.Alert.success(`${count} item${count > 1 ? 's' : ''} paused`);
      setSelectedRows([]);
      refetchCadences();
    });
  };

  const handleBulkActivate = () => {
    const eligibleRows = selectedRows.filter((row) => {
      const s = row.status?.toLowerCase();
      return (s === 'paused' || s === 'draft') && !!row._raw?.canDelete;
    });
    if (eligibleRows.length === 0) return;
    const count = eligibleRows.length;
    Promise.allSettled(eligibleRows.map((row) => setCadenceStatus(row.id, row.status, 'active'))).then((results) => {
      const failedResults = results.filter((r) => r.status === 'rejected');
      const failed = failedResults.length;
      if (failed > 0) {
        const hasAttachmentError = failedResults.some((r) => r.reason?.code === 'ATTACHMENT_TOO_LARGE');
        if (hasAttachmentError && failed === 1) {
          EverTrue.Alert.error(
            'Activation Failed: One or more automated email steps include an attachment that is over the 7MB limit. This cadence cannot be activated.'
          );
        } else if (hasAttachmentError) {
          EverTrue.Alert.error(
            `${failed} of ${count} activations failed — one or more include an attachment over the 7MB limit.`
          );
        } else {
          EverTrue.Alert.error(`${failed} of ${count} activations failed`);
        }
      } else {
        EverTrue.Alert.success(`${count} item${count > 1 ? 's' : ''} activated`);
      }
      setSelectedRows([]);
      refetchCadences();
    });
  };

  /** Actions object passed to all table components. */
  const actions = {
    onView: handleView,
    onEdit: handleEdit,
    onStartRename: handleStartRename,
    onDuplicate: handleDuplicate,
    onDelete: handleDelete,
    onStatusToggle: handleStatusToggle,
    onSaveAsShell: handleSaveAsShell,
    onCreateFromShell: handleCreateFromShell,
    onShare: handleShare,
    onRestore: handleRestore,
  };

  const renamingState = {
    renamingId,
    renameValue,
    onRenameValueChange: setRenameValue,
    onSubmitRename: handleSubmitRename,
    onCancelRename: () => setRenamingId(null),
  };

  // --- Create dropdown ---
  const renderCreateDropdown = () => {
    const items = [
      {
        label: 'New Cadence',
        icon: 'cadences',
        iconKit: 'gt2',
        description: 'Multi-step outreach sequence',
        onClick: () => handleCreateNew('cadence'),
      },
      {
        label: 'New Prospect Plan',
        icon: 'person',
        description: 'Personalized donor journey',
        onClick: () => handleCreateNew('prospect-plan'),
      },
      hasBulkMessaging && {
        label: 'New Bulk Message',
        icon: 'email',
        description: 'One-time mass email',
        onClick: () => handleCreateNew('bulk-message'),
      },
      {
        label: 'New Blueprint',
        icon: 'content-copy',
        iconKit: 'gt2',
        description: 'Reusable cadence template',
        onClick: () => handleCreateNew('blueprint'),
      },
    ].filter(Boolean);

    return (
      <Popover2
        trigger={
          <span className="et--button">
            <Icon icon="add" /> Create New
          </span>
        }
        contentClassName="cadences-v2-create-dropdown"
      >
        {({ closePopover }) => (
          <div className="cadences-v2-create-dropdown--menu">
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                className="cadences-v2-create-dropdown--item"
                onClick={() => {
                  closePopover();
                  item.onClick();
                }}
              >
                <div className="cadences-v2-create-dropdown--item-icon">
                  <Icon icon={item.icon} iconKit={item.iconKit} />
                </div>
                <div className="cadences-v2-create-dropdown--item-content">
                  <span className="cadences-v2-create-dropdown--item-label">{item.label}</span>
                  <span className="cadences-v2-create-dropdown--item-desc">{item.description}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </Popover2>
    );
  };

  /** Per-tab filter definitions for the shared FilterBar. */
  const getTabFilterDefs = () => {
    const statusDef = {
      id: 'status',
      label: 'Status',
      type: 'multiselect',
      options: STATUS_OPTIONS_BY_TAB[selectedTab],
      value: statusFilter,
      onChange: setStatusFilter,
      alwaysVisible: true,
      alwaysShowLabel: true,
    };

    const createdByDef = {
      id: 'createdBy',
      label: 'Created By',
      type: 'multiselect',
      options: allOwnerOptions,
      value: ownerFilter,
      onChange: setOwnerFilter,
      alwaysVisible: true,
      alwaysShowLabel: true,
      searchable: true,
      loading: usersLoading || (allOwnerOptions.length === 0 && (cadences.length > 0 || rawTemplates.length > 0)),
    };

    const tagsDef = {
      id: 'tags',
      label: 'Tags',
      type: 'multiselect',
      options: tagOptions,
      value: selectedTags,
      onChange: setSelectedTags,
      alwaysVisible: true,
      alwaysShowLabel: true,
      searchable: true,
    };

    const creationDateDef = {
      id: 'creationDate',
      label: 'Creation Date',
      type: 'date',
      value: dateFilter,
      onChange: setDateFilter,
      alwaysVisible: true,
    };

    const metricDef = {
      id: 'successMetric',
      label: 'Primary Success Metric',
      type: 'multiselect',
      options: METRIC_OPTIONS,
      value: metricFilter,
      onChange: setMetricFilter,
      alwaysVisible: false,
      alwaysShowLabel: true,
    };

    const titleDef = {
      id: 'title',
      label: 'Title',
      type: 'text',
      value: titleFilter,
      onChange: setTitleFilter,
      alwaysVisible: false,
    };

    const blueprintDef = {
      id: 'blueprint',
      label: 'Cadence Blueprint',
      type: 'multiselect',
      options: blueprintOptions,
      value: blueprintFilter
        ? [{ label: templatesMap[blueprintFilter] || `#${blueprintFilter}`, value: blueprintFilter }]
        : [],
      onChange: (val) => setBlueprintFilter(val?.[0]?.value || null),
      alwaysVisible: !!blueprintFilter,
      alwaysShowLabel: true,
      searchable: true,
    };

    switch (selectedTab) {
      case 'outreach':
        return [
          statusDef,
          createdByDef,
          tagsDef,
          creationDateDef,
          {
            id: 'type',
            label: 'Type',
            type: 'multiselect',
            options: CADENCE_TYPE_OPTIONS,
            value: typeFilter,
            onChange: setTypeFilter,
            alwaysVisible: false,
            alwaysShowLabel: true,
          },
          metricDef,
          {
            id: 'numericFilter',
            label: '# Current Contacts',
            type: 'numeric',
            value: numericFilter,
            onChange: setNumericFilter,
            alwaysVisible: false,
          },
          titleDef,
          blueprintDef,
        ];
      case 'bulk':
        return [
          statusDef,
          createdByDef,
          tagsDef,
          {
            id: 'sendDate',
            label: 'Send Date',
            type: 'date',
            value: sendDateFilter,
            onChange: setSendDateFilter,
            alwaysVisible: true,
          },
          metricDef,
          {
            id: 'numericFilter',
            label: 'Total Recipients',
            type: 'numeric',
            value: numericFilter,
            onChange: setNumericFilter,
            alwaysVisible: false,
          },
          titleDef,
          { ...creationDateDef, alwaysVisible: false },
          blueprintDef,
        ];
      case 'templates':
        return [
          statusDef,
          {
            id: 'type',
            label: 'Type',
            type: 'multiselect',
            options: BLUEPRINT_TYPE_OPTIONS,
            value: typeFilter,
            onChange: setTypeFilter,
            alwaysVisible: true,
            alwaysShowLabel: true,
          },
          createdByDef,
          tagsDef,
          creationDateDef,
          metricDef,
          {
            id: 'numericFilter',
            label: 'Total Cadences',
            type: 'numeric',
            value: numericFilter,
            onChange: setNumericFilter,
            alwaysVisible: false,
          },
          titleDef,
        ];
      default:
        return [];
    }
  };

  // --- Active table component ---
  const renderActiveTable = () => {
    if (isLoading) {
      return (
        <div className="cadences-v2-loading">
          <Loading />
        </div>
      );
    }

    if (fetchError) {
      return (
        <div className="cadences-v2-error-state">
          <Icon icon="error-outline" size={2} />
          <h3>Failed to load data</h3>
          <p>{fetchError}</p>
          <Button type="secondary" onClick={selectedTab === 'templates' ? refetchTemplates : refetchCadences}>
            Retry
          </Button>
        </div>
      );
    }

    const sharedProps = { currentUserId, actions, renamingState, onSelectionChange: setSelectedRows };

    switch (selectedTab) {
      case 'outreach':
        return <OutreachPlansTable data={filteredOutreachPlans} loading={false} {...sharedProps} />;
      case 'bulk':
        return <BulkMessagesTable data={filteredBulkMessages} loading={false} {...sharedProps} />;
      case 'templates':
        return <CadenceShellsTable data={filteredTemplates} loading={false} {...sharedProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="cadences-v2-page">
      <div className="cadences-v2-header">
        <PageHeader
          title="Cadences 2.0"
          actions={
            <div className="cadences-v2-header--actions">
              {renderCreateDropdown()}
              <Button type="simple" href="settings/cadences">
                <Icon icon="settings" />
              </Button>
            </div>
          }
        />
      </div>

      <div className="cadences-v2-tab-nav">
        {tabs.map((tab) => (
          <NavButton key={tab.id} active={selectedTab} activeWhen={tab.id} onSelect={() => setSelectedTab(tab.id)}>
            {tab.label}
          </NavButton>
        ))}
      </div>

      {domainStatus === 'pending' && selectedTab === 'outreach' && (
        <div className="cadences-v2--pending-domain-banner">
          <Icon icon="warning" size={1} />
          <span>
            Sending domain pending verification. Email steps in your cadences are paused until your domain is verified.
            DNS verification can take up to 48 hours.
          </span>
        </div>
      )}

      {showAutoResumeBanner && selectedTab === 'outreach' && (
        <div className="cadences-v2--auto-resume-banner">
          <div className="cadences-v2--auto-resume-banner--content">
            <Icon icon="check-circle" size={1} />
            <span>
              <strong>{dnsPauseEvent.count} Cadences Automatically Resumed</strong>
              {' — '}
              Cadences linked to <strong>{dnsPauseEvent.domainName || postmarkDomain?.name}</strong> were automatically
              resumed
              {postmarkDomain?.dkim_verified_at
                ? ` on ${new Date(postmarkDomain.dkim_verified_at).toLocaleDateString()}`
                : ''}
              . Cadences that were manually paused before the DNS change were not automatically resumed.
            </span>
          </div>
          <button
            type="button"
            className="cadences-v2--auto-resume-banner--dismiss"
            onClick={handleDismissAutoResumeBanner}
            aria-label="Dismiss"
          >
            <Icon icon="close" size={1} />
          </button>
        </div>
      )}

      <FilterBar filters={getTabFilterDefs()} />

      {promptAction && (
        <div className="cadence-shell-name-bar">
          <label>{promptAction.type === 'shell' ? 'Blueprint name:' : 'New cadence name:'}</label>
          <input
            type="text"
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handlePromptSubmit();
              if (e.key === 'Escape') setPromptAction(null);
            }}
            autoFocus
          />
          <Button type="action" onClick={handlePromptSubmit}>
            {promptAction.type === 'shell' ? 'Save' : 'Create'}
          </Button>
          <Button type="simple" onClick={() => setPromptAction(null)}>
            Cancel
          </Button>
        </div>
      )}

      {selectedRows.length > 0 && (
        <div className="cadences-v2-bulk-action-bar">
          <span className="cadences-v2-bulk-action-bar--count">
            <Icon icon="check-circle" size={1} /> {selectedRows.length} selected
          </span>
          <div className="cadences-v2-bulk-action-bar--actions">
            {selectedTab === 'outreach' && selectedRows.some((r) => r._raw?.canDelete) && (
              <Button
                type="secondary"
                onClick={handleBulkActivate}
                disabled={
                  !selectedRows.some((r) => {
                    const s = r.status?.toLowerCase();
                    return (s === 'paused' || s === 'draft') && !!r._raw?.canDelete;
                  })
                }
              >
                Activate
              </Button>
            )}
            {selectedTab !== 'templates' && selectedRows.some((r) => r._raw?.canDelete) && (
              <Button
                type="secondary"
                onClick={handleBulkPause}
                disabled={!selectedRows.some((r) => r.status?.toLowerCase() === 'active' && !!r._raw?.canDelete)}
              >
                Pause
              </Button>
            )}
            {selectedRows.some((r) => r._raw?.canDelete) && (
              <Button type="secondary" onClick={handleBulkDelete}>
                Archive
              </Button>
            )}
          </div>
          <button
            type="button"
            className="cadences-v2-bulk-action-bar--close"
            onClick={() => setSelectedRows([])}
            aria-label="Clear selection"
          >
            <Icon icon="close" size={1} />
          </button>
        </div>
      )}

      <div
        className="cadences-v2-table-container"
        style={{ height: 'calc(100vh - 270px)', width: '100%', overflowX: 'auto' }}
      >
        {renderActiveTable()}
      </div>

      <CadenceSharingModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareTarget(null);
          refetchCadences();
        }}
        cadenceId={shareTarget?.id}
        cadenceVersion={shareTarget?.rawCadence?.version}
        cadenceName={shareTarget?.rawCadence?.name}
        ownerId={shareTarget?.rawCadence?.ownerUserId}
      />

      <DuplicateCadenceModal
        isOpen={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        onDuplicate={handleDuplicateConfirm}
        entityName={duplicateTarget?.name}
        entityType={duplicateTarget?.type}
        sourceArchived={duplicateTarget?.archived}
      />

      <ConfirmModal
        isOpen={!!confirmConfig}
        onClose={() => setConfirmConfig(null)}
        title={confirmConfig?.title || ''}
        body={confirmConfig?.body || ''}
        confirmLabel={confirmConfig?.confirmLabel}
        isDanger={confirmConfig?.isDanger}
        onConfirm={confirmConfig?.onConfirm || (() => {})}
      />
    </div>
  );
};

export default CadencesV2Controller;
